import { useCallback, useEffect, useState } from 'react'
import api from '@/lib/axiosInstance'

/**
 * The agent's own choices, saved once and used everywhere: what happens to a
 * listing generated from the AI review, whether owners are asked to validate
 * it, and the defaults a new listing starts with.
 */
export type ListingOnGenerate = 'draft' | 'submit' | 'publish'

export interface AgentSettings {
    listingOnGenerate: ListingOnGenerate
    requireOwnerValidation: number
    defaultRadius: number
    coAgencyByDefault: number
    coShareByDefault: number
    /** When the agent accepted the AI terms, and which version. */
    aiTermsVersion: string | null
    aiTermsAcceptedAt: string | null
}

/** What can be sent to save: the settings, or accepting the AI terms. */
export type AgentSettingsPatch = Partial<AgentSettings> & { acceptAiTerms?: boolean }

export const DEFAULT_SETTINGS: AgentSettings = {
    listingOnGenerate: 'draft',
    requireOwnerValidation: 0,
    defaultRadius: 200,
    coAgencyByDefault: 0,
    coShareByDefault: 0,
    aiTermsVersion: null,
    aiTermsAcceptedAt: null,
}

export const getAgentSettings = async (): Promise<AgentSettings> => {
    const { data } = await api.get('/me/settings')
    return data?.status ? (data.settings as AgentSettings) : DEFAULT_SETTINGS
}

export const saveAgentSettings = async (patch: AgentSettingsPatch): Promise<AgentSettings> => {
    const { data } = await api.post('/me/settings', patch, { headers: { 'Content-Type': 'application/json' } })
    if (!data?.status) throw new Error(data?.error ?? 'Your settings could not be saved.')
    return data.settings as AgentSettings
}

/** The settings as they are now; the defaults until they have loaded. */
export function useAgentSettings() {
    const [settings, setSettings] = useState<AgentSettings>(DEFAULT_SETTINGS)
    const [loaded, setLoaded] = useState(false)

    useEffect(() => {
        let cancelled = false
        getAgentSettings()
            .then((next) => !cancelled && setSettings(next))
            .catch(() => {})
            .finally(() => !cancelled && setLoaded(true))
        return () => {
            cancelled = true
        }
    }, [])

    const save = useCallback(async (patch: AgentSettingsPatch) => {
        const next = await saveAgentSettings(patch)
        setSettings(next)
        return next
    }, [])

    return { settings, loaded, save }
}
